import {
  DeviceLimits,
  DynamicYearlyProgramRewards,
  ProgramRewardsDeviceAmount,
} from '@/types/programs-new/programs-new'
export type SelectedDeviceCounts = {
  vehicleCount: number
  thermostatCount: number
  chargerCount: number
}
export function getMinMaxRangeString(
  rewardDeviceAmounts: ProgramRewardsDeviceAmount[],
) {
  const minValue = Math.min(
    ...rewardDeviceAmounts.map(
      (r) => r.dollarAmountYearly ?? r.dollarAmount ?? 0,
    ),
  )
  const maxValue = Math.max(
    ...rewardDeviceAmounts.map(
      (r) => r.dollarAmountYearly ?? r.dollarAmount ?? 0,
    ),
  )
  const minValueString = Number.isInteger(minValue)
    ? minValue.toString()
    : minValue.toFixed(2)
  const maxValueString = Number.isInteger(maxValue)
    ? maxValue.toString()
    : maxValue.toFixed(2)
  return minValue !== maxValue
    ? `${minValueString}-${maxValueString}`
    : maxValueString
}

export function calculateCurrentTotal(
  key: keyof DynamicYearlyProgramRewards,
  rewardsByCategory: DynamicYearlyProgramRewards,
  devicesSelected: SelectedDeviceCounts,
) {
  const vehicleTotal =
    (
      rewardsByCategory[key]?.filter((e) => e.deviceType === 'vehicle') ?? []
    ).reduce(
      (total, curr) =>
        total + (curr.dollarAmountYearly ?? curr.dollarAmount ?? 0),
      0,
    ) * devicesSelected.vehicleCount
  const thermostatTotal =
    (
      rewardsByCategory[key]?.filter((e) => e.deviceType === 'thermostat') ?? []
    ).reduce(
      (total, curr) =>
        total + (curr.dollarAmountYearly ?? curr.dollarAmount ?? 0),
      0,
    ) * devicesSelected.thermostatCount
  const chargerTotal =
    (
      rewardsByCategory[key]?.filter(
        (e) => e.deviceType === 'vehicleCharger',
      ) ?? []
    ).reduce(
      (total, curr) =>
        total + (curr.dollarAmountYearly ?? curr.dollarAmount ?? 0),
      0,
    ) * devicesSelected.chargerCount
  return vehicleTotal + thermostatTotal + chargerTotal
}

export function calculateDynamicRewardCategoryTotals(
  rewardsByCategory: DynamicYearlyProgramRewards,
  devicesSelected: SelectedDeviceCounts,
  rewardDeviceLimits: DeviceLimits | null,
) {
  // First, consider device-level limits and cut off anything beyond the max
  const slicedDeviceCounts: SelectedDeviceCounts = {
    vehicleCount: Math.min(
      devicesSelected.vehicleCount,
      rewardDeviceLimits?.vehicle?.max ?? Infinity,
    ),
    thermostatCount: Math.min(
      devicesSelected.thermostatCount,
      rewardDeviceLimits?.thermostat?.max ?? Infinity,
    ),
    chargerCount: Math.min(
      devicesSelected.chargerCount,
      rewardDeviceLimits?.charger?.max ?? Infinity,
    ),
  }

  // Now, take that sliced count and do the same check for total devices.
  // The frontend selection logic sorts by vehicle, thermostat, charger,
  // so we will too -- that is the order in which we consider totals.
  let acc = 0
  for (const key in slicedDeviceCounts) {
    const remainder = rewardDeviceLimits?.total_devices?.max
      ? rewardDeviceLimits.total_devices.max - acc
      : Infinity
    slicedDeviceCounts[key as keyof SelectedDeviceCounts] = Math.min(
      slicedDeviceCounts[key as keyof SelectedDeviceCounts],
      remainder,
    )
    acc += slicedDeviceCounts[key as keyof SelectedDeviceCounts]
  }

  /**
   * When showing in the UI card we split this to two the order is as such:
   * - Enrollment/Application
   * - Participation
   * - End of Program
   */
  return [
    rewardsByCategory.enrollment && {
      label: 'Enrollment',
      currentTotal: calculateCurrentTotal(
        'enrollment',
        rewardsByCategory,
        slicedDeviceCounts,
      ),
      suffix: 'total',
    },
    rewardsByCategory.application && {
      label: 'Application',
      currentTotal: calculateCurrentTotal(
        'application',
        rewardsByCategory,
        slicedDeviceCounts,
      ),
      suffix: 'total',
    },
    rewardsByCategory.participation && {
      label: 'Participation',
      currentTotal: calculateCurrentTotal(
        'participation',
        rewardsByCategory,
        slicedDeviceCounts,
      ),
      suffix: 'total/year',
    },
    rewardsByCategory.endOfProgram && {
      label: 'End of Program',
      currentTotal: calculateCurrentTotal(
        'endOfProgram',
        rewardsByCategory,
        slicedDeviceCounts,
      ),
      suffix: 'total',
    },
  ].filter((r) => r !== undefined)
}
